/**
 * The copilot's own highlight channel: the paragraphs a message being
 * written takes as its context, marked in the Document view
 * (`data-copilot-marked`) — kept apart from the evidence tints
 * (`anchorHighlight`), which the same view sets. A module-level slot and a
 * DOM event, as `anchorHighlight` does, so the column and the view needn't
 * share props.
 */

import { useEffect, useLayoutEffect, useState, type RefObject } from 'react';

export const COPILOT_MARK_EVENT = 'gti:copilot-mark';
const ATTRIBUTE = 'data-copilot-marked';

let marked: string[] = [];

/** Mark these paragraphs (Word paragraph ids); `[]` clears the mark. */
export function markCopilotParagraphs(paraIds: string[]): void {
  marked = paraIds.map((id) => id.toUpperCase());
  window.dispatchEvent(new CustomEvent(COPILOT_MARK_EVENT));
}

export function copilotMarkedParagraphs(): string[] {
  return marked;
}

/** Keep `root`'s marked paragraphs marked — after every render too, as a
 * re-rendered block loses an attribute set from here. */
export function useCopilotMarks(rootRef: RefObject<HTMLElement | null>): void {
  const [, setTick] = useState(0);
  useEffect(() => {
    const changed = () => setTick((n) => n + 1);
    window.addEventListener(COPILOT_MARK_EVENT, changed);
    return () => window.removeEventListener(COPILOT_MARK_EVENT, changed);
  }, []);
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || marked.length === 0) {
      return undefined;
    }
    const wanted = new Set(marked);
    const applied: HTMLElement[] = [];
    for (const el of root.querySelectorAll<HTMLElement>('[data-para-id]')) {
      if (wanted.has((el.dataset.paraId ?? '').toUpperCase())) {
        el.setAttribute(ATTRIBUTE, '');
        applied.push(el);
      }
    }
    return () => applied.forEach((el) => el.removeAttribute(ATTRIBUTE));
  });
}

/** Test hook: reset module state between tests. */
export function clearCopilotMarks(): void {
  marked = [];
}
